import { useCallback, useState } from "react";

import { Button2 } from "@/ui2/atoms/Button";
import { CosignaturePermissionMetadata, CosignatureRequirementReason, UUID } from "@/core/types";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import { Heading2 } from "@/ui2/atoms/Heading";
import { Switch2 } from "@/ui2/molecules/Switch";
import { useCosignatureSupervisors } from "@/features/cosignature/api/queries/useCosignatureSupervisors";
import { useUpdateUserPermissions } from "@/features/permissions/api/mutations/useUpdateUserPermissions";
import { useUserPermissions } from "@/features/permissions/api/queries/useUserPermissions";

import { AssignedSupervisor } from "./components/AssignedSupervisor";
import { CosignatureFormValues } from "./(modal)/schema";
import { EnabledByLabel } from "./components/EnabledByLabel";
import { RequireCosignatureModal } from "./(modal)/RequireCosignatureModal";
import { RequireCosignatureSkeleton } from "./components/RequireCosignatureSkeleton";
import { showCosignatureToast } from "./helpers/toast-copy";

type Props = {
  isDisabled?: boolean;
  isRequired?: boolean;
  metadata: CosignaturePermissionMetadata | null;
  providerName: string;
  userId: UUID;
};

function RequireCosignature({ isDisabled, isRequired, metadata, providerName, userId }: Props) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalInitialValues, setModalInitialValues] = useState<
    Partial<CosignatureFormValues> | undefined
  >(undefined);
  const { isPending, mutate: updatePermissions } = useUpdateUserPermissions(userId, providerName);

  useCosignatureSupervisors(); // prefetch supervisors

  const openModal = useCallback(() => {
    if (isPending) return;

    setModalInitialValues(
      metadata
        ? {
            requirementReason: metadata.requirement_reason,
            requirementReasonDetails: metadata.requirement_reason_details ?? "",
            supervisorId: metadata.assigned_supervisor_id,
          }
        : undefined,
    );
    setIsModalOpen(true);
  }, [metadata, isPending]);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleToggle = useCallback(
    (value: boolean) => {
      if (isPending) return;

      if (value) return openModal();

      updatePermissions(
        { co_sign: { is_required: false } },
        { onSuccess: () => showCosignatureToast({ isCosignatureRequired: false, providerName }) },
      );
    },
    [openModal, providerName, updatePermissions, isPending],
  );

  const handleApprove = useCallback(
    (data: CosignatureFormValues) => {
      updatePermissions(
        {
          co_sign: {
            assigned_supervisor_id: data.supervisorId as UUID,
            is_required: true,
            requirement_reason: data.requirementReason as CosignatureRequirementReason,
            requirement_reason_details:
              data.requirementReason === "Other" ? data.requirementReasonDetails : null,
          },
        },
        {
          onSuccess: () => {
            closeModal();
            showCosignatureToast({ isCosignatureRequired: true, providerName });
          },
        },
      );
    },
    [closeModal, providerName, updatePermissions],
  );

  return (
    <>
      <DashboardCard2
        Actions={
          metadata &&
          !isDisabled && (
            <Button2 onPress={openModal} size="sm" variant="link">
              Edit
            </Button2>
          )
        }
        classNameChildren="tw:flex tw:flex-col tw:gap-4"
        data-public="true"
        title="Require Co-Sign"
      >
        <Switch2
          className="tw:items-start tw:gap-5.5"
          isDisabled={isDisabled}
          isSelected={isRequired}
          onChange={handleToggle}
        >
          <div className="tw:flex tw:flex-col tw:gap-2">
            <Heading2 as="h3" size={1}>
              Co-Sign Required
            </Heading2>

            <p className="body-md">
              All clinical notes from this provider will need co-signature from an approved
              supervisor before being finalized.
            </p>
          </div>
        </Switch2>

        {metadata && (
          <div className="tw:ml-18.5 tw:flex tw:flex-col tw:gap-4">
            <EnabledByLabel
              requirementReason={metadata.requirement_reason}
              requirementReasonDetails={metadata?.requirement_reason_details}
              stateUpdatedAt={metadata.state_updated_at}
              stateUpdatedByName={metadata.state_updated_by_name}
            />

            <AssignedSupervisor assignedSupervisorName={metadata.assigned_supervisor_name} />
          </div>
        )}
      </DashboardCard2>

      <RequireCosignatureModal
        initialValues={modalInitialValues}
        isOpen={isModalOpen}
        onClose={closeModal}
        onSubmit={handleApprove}
        userId={userId}
      />
    </>
  );
}

export function RequireCosignatureGate({
  isDisabled,
  providerName,
  userId,
}: {
  isDisabled: boolean;
  providerName: string;
  userId: UUID;
}) {
  const { data: permissions, isError, isLoading } = useUserPermissions({ userId });

  if (isLoading) return <RequireCosignatureSkeleton />;
  if (isError || !permissions) return null;

  const { co_sign } = permissions;

  const metadata = co_sign.is_required ? co_sign : null;

  return (
    <RequireCosignature
      isDisabled={isDisabled}
      isRequired={co_sign.is_required}
      metadata={metadata}
      providerName={providerName}
      userId={userId}
    />
  );
}
